import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import useStorefrontCurrency from "@features/ecommerce/hooks/useStorefrontCurrency";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { AppendIfPrefixExists } from "modules/sitePaths";
import type CatalogedProduct from "shared-ui/src/types/ecommerce/CatalogedProduct";
import type { CartItemAvailabilityDto } from "@lib/ShiOneClient";
import useGetRecommendedAddOns from "@features/ecommerce/cart/useGetRecommendedAddOns";
import AddOnsPanel from "@features/ecommerce/cart/AddOnsPanel";
import useGetShoppingCartLines from "@features/ecommerce/cart/useGetShoppingCartLines";
import useCartLineMutations from "@features/ecommerce/cart/useCartLineMutations";
import useGetCartProductAvailability from "@features/unified-user-header-feature/hooks/useGetCartProductAvailability";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { QuantitySelector } from "shared-ui/src/components/ecommerce/product-detail-drawer/QuantitySelector";

const baseUrl = getShiComBaseUrl();

interface AvailabilityStatusProps {
  availability?: string | null;
  checkedAvailability?: CartItemAvailabilityDto;
  isChecking?: boolean;
  hasChecked?: boolean;
}

function AvailabilityStatus({
  availability,
  checkedAvailability,
  isChecking,
  hasChecked,
}: AvailabilityStatusProps) {
  // stockDataAvailable === false means no stock source returned any rows, so inStock of
  // false is "unknown" rather than a negative result. Fall back to the catalog status
  // instead of reporting Backorder.
  const hasStockData =
    checkedAvailability != null &&
    checkedAvailability.stockDataAvailable !== false;
  // Once the check has answered, only the status it returned is trustworthy — the cached
  // catalog string the modal was opened with is stale at that point.
  const catalogStatus =
    checkedAvailability?.itemStatus ?? (hasChecked ? undefined : availability);

  const label = isChecking
    ? "Updating…"
    : hasStockData
      ? checkedAvailability?.inStock
        ? checkedAvailability.totalUnits != null
          ? `In Stock (${checkedAvailability.totalUnits} units)`
          : "In Stock"
        : checkedAvailability?.itemStatus || "Backorder"
      : (catalogStatus ?? (hasChecked ? "Availability unavailable" : undefined));

  if (!label) {
    return null;
  }

  // Must mirror the loose inStock truthiness used for the label above — a strict
  // boolean/exact-string check can disagree with a label that still reads "In Stock".
  // includes() (not equality/startsWith) so variants like "250+ In Stock" match;
  // SHI's negative statuses ("Backordered", "Out of Stock") don't contain the phrase.
  const isInStock =
    !isChecking &&
    (hasStockData
      ? Boolean(checkedAvailability?.inStock)
      : (catalogStatus?.toLowerCase().includes("in stock") ?? false));

  // Red dot only for a definitive negative from the availability check;
  // "Availability unavailable"/unknown statuses keep the neutral dot.
  const isBackordered = !isChecking && !isInStock && hasStockData;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {isChecking ? (
        <RefreshIcon
          aria-hidden="true"
          data-testid="add-to-cart-availability-refresh"
          sx={{
            fontSize: 14,
            color: "text.secondary",
            flexShrink: 0,
            animation: "cart-availability-spin 1s linear infinite",
            "@keyframes cart-availability-spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        />
      ) : (
        <FiberManualRecordIcon
          aria-hidden="true"
          data-testid="add-to-cart-availability-dot"
          data-status={
            isInStock ? "in-stock" : isBackordered ? "backordered" : "unavailable"
          }
          sx={{
            // ~6px rendered dot: the glyph circle spans ~2/3 of the icon's em box.
            fontSize: 9,
            color: isInStock
              ? "success.main"
              : isBackordered
                ? "error.main"
                : "text.disabled",
            flexShrink: 0,
          }}
        />
      )}
      {/* Spec: rgba(0,0,0,0.54) for every status — shiPalette's text.secondary
          (#0000008A) is that exact value. */}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export interface AddToCartModalProps {
  open: boolean;
  /** The just-added product; null while closed. */
  product: CatalogedProduct | null;
  /** Quantity that was added (single-item add is always 1). */
  quantity?: number;
  onClose: () => void;
}

/**
 * Add-to-cart confirmation modal (SPU-534).
 *
 * Fires after a single-item add (not Configure/config-system products). Per the epic
 * spec it confirms only the just-added item — image, name, quantity, unit price, and
 * updated stock — with no cart totals. Exits: Continue Shopping (dismiss), View Cart
 * (in-app cart page), and Checkout (SHI.com handoff). The add-ons upsell that layers
 * into this modal is SPU-535.
 */
export default function AddToCartModal({
  open,
  product,
  quantity = 1,
  onClose,
}: AddToCartModalProps) {
  const { formatAmount } = useStorefrontCurrency();
  const navigate = useNavigate();
  // SPU-527: "View Cart" navigates to the in-app /cart page only when it is enabled;
  // otherwise it falls back to the pre-existing SHI.com cart (parity with the mini-cart).

  const productItemId = Number(product?.productDescriptor);
  const validProductItemId = Number.isSafeInteger(productItemId) && productItemId > 0
    ? productItemId
    : undefined;

  const { data: addOns } = useGetRecommendedAddOns(
    product?.productDescriptor,
    product?.productClass,
    open && product != null,
  );
  const {
    data: availability,
    isFetching: isAvailabilityFetching,
    isFetched: hasFetchedAvailability,
  } = useGetCartProductAvailability(
    validProductItemId == null ? [] : [validProductItemId],
    open && validProductItemId != null,
    { staleTime: 0 },
  );
  const {
    data: cartLines,
    isError: cartLinesError,
    isFetchedAfterMount: cartLinesFetchedAfterMount,
  } = useGetShoppingCartLines();
  const { updateQuantity } = useCartLineMutations();
  const [warrantyMutationInFlight, setWarrantyMutationInFlight] = useState(false);

  useEffect(() => {
    if (!open) {
      setWarrantyMutationInFlight(false);
    }
  }, [open]);

  const matchingCartLines = useMemo(() => {
    const productId = Number(product?.productDescriptor);
    if (!Number.isSafeInteger(productId)) {
      return [];
    }
    return (cartLines ?? []).filter(
      (line) => line.itemId === productId || line.baseSystemId === productId,
    );
  }, [cartLines, product?.productDescriptor]);

  // The SHI.com cart appends a new line for a repeat add instead of merging quantities,
  // so the same product can legitimately occupy several lines. The highest line number is
  // the one this modal just added, and it is the only line the stepper should touch.
  const addedCartLine = useMemo(() => {
    if (!cartLinesFetchedAfterMount || cartLinesError) {
      return undefined;
    }
    return matchingCartLines.reduce<(typeof matchingCartLines)[number] | undefined>(
      (newest, line) =>
        newest == null || (line.lineNumber ?? -1) > (newest.lineNumber ?? -1)
          ? line
          : newest,
      undefined,
    );
  }, [cartLinesError, cartLinesFetchedAfterMount, matchingCartLines]);

  const cartQuantity = addedCartLine?.quantity ?? quantity;
  const [selectedQuantity, setSelectedQuantity] = useState(cartQuantity);
  const checkedAvailability = availability?.find(
    (item) => item.itemId === validProductItemId,
  );

  useEffect(() => {
    setSelectedQuantity(cartQuantity);
  }, [cartQuantity]);

  const goToCart = () => {
    onClose();
    navigate(AppendIfPrefixExists("/cart"));
  };

  const changeQuantity = (nextQuantity: number) => {
    if (addedCartLine?.lineNumber == null || nextQuantity < 1) {
      return;
    }

    setSelectedQuantity(nextQuantity);
    updateQuantity.mutate({
      lineNumber: addedCartLine.lineNumber,
      quantity: nextQuantity,
      syncWarranty: true,
    });
  };

  return (
    <Dialog
      open={open && product != null}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxWidth: 640, borderRadius: 2 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleOutlineIcon sx={{ color: "success.main", fontSize: 24 }} />
            <Typography variant="h6" fontWeight={500}>
              Added to cart
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label="Close"
            onClick={onClose}
            sx={{ color: "action.active" }}
            {...telemetryProps({
              name: "add_to_cart_close",
              section: "add_to_cart",
              label: "Close Added to Cart",
            })}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        // No explicit max height: an expanded warranty list renders every row, and the dialog
        // paper's own viewport cap is what bounds the modal.
        sx={{ px: 2, py: 2, bgcolor: "background.default" }}
      >
        {product && (
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {product.imageUrl ? (
                <Box
                  component="img"
                  src={product.imageUrl}
                  alt={product.name ?? "Product"}
                  sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <ImageOutlinedIcon sx={{ fontSize: 28, color: "text.disabled" }} />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={400}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {product.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
                <AvailabilityStatus
                  availability={product.availability}
                  checkedAvailability={checkedAvailability}
                  isChecking={isAvailabilityFetching}
                  hasChecked={hasFetchedAvailability}
                />
                <Typography variant="caption" fontWeight={500} color="secondary.main">
                  {formatAmount(Number(product.price ?? 0))}
                </Typography>
              </Box>
            </Box>

            <QuantitySelector
              value={selectedQuantity}
              committedValue={cartQuantity}
              disabled={
                updateQuantity.isLoading ||
                warrantyMutationInFlight ||
                addedCartLine?.lineNumber == null
              }
              onDecrease={() => changeQuantity(selectedQuantity - 1)}
              onIncrease={() => changeQuantity(selectedQuantity + 1)}
              onValueChange={setSelectedQuantity}
              onValueCommit={changeQuantity}
              decreaseTelemetry={{
                name: "add_to_cart_decrease_quantity",
                section: "add_to_cart",
                label: "Decrease Quantity",
              }}
              increaseTelemetry={{
                name: "add_to_cart_increase_quantity",
                section: "add_to_cart",
                label: "Increase Quantity",
              }}
            />
          </Box>
        )}

        {product && (
          <AddOnsPanel
            product={product}
            quantity={cartQuantity}
            disabled={updateQuantity.isLoading || warrantyMutationInFlight}
            onWarrantyMutationChange={setWarrantyMutationInFlight}
            accessories={addOns?.accessories?.items}
            warranties={addOns?.warranties?.items}
            accessoriesHasMore={addOns?.accessories?.hasMore}
            warrantiesTotalCount={addOns?.warranties?.totalCount}
          />
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1.5,
          flexDirection: { xs: "column", sm: "row" },
          "& > *": { flex: 1, width: { xs: "100%", sm: "auto" } },
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          {...telemetryProps({
            name: "add_to_cart_continue_shopping",
            section: "add_to_cart",
            label: "Continue Shopping",
          })}
        >
          Continue Shopping
        </Button>
        <Button
          variant="outlined"
          onClick={goToCart}
          {...telemetryProps({
            name: "add_to_cart_view_cart",
            section: "add_to_cart",
            label: "View Cart",
          })}
        >
          View Cart
        </Button>
        <Button
          variant="contained"
          href={`${baseUrl}/CheckOut/OrderRequest.aspx`}
          {...telemetryProps({
            name: "add_to_cart_checkout",
            section: "add_to_cart",
            label: "Checkout",
          })}
        >
          Checkout
        </Button>
      </DialogActions>
    </Dialog>
  );
}
